<template>
  <div class="w-full group">
    <label
      v-if="!noLabel"
      :for="id"
      :class="[
        !ghostLabel
          ? 'dark:text-gray-300 text-primary font-medium'
          : required
            ? 'text-primary dark:text-gray-300 font-semibold'
            : 'text-gray-700 dark:text-gray-200 font-normal',
      ]"
      class="flex items-center gap-1 mb-1 pl-4 text-sm h-min-5"
    >
      <slot name="label">
        {{ $t(label || '') }}
      </slot>

      <span v-if="required" class="text-danger dark:text-danger text-lg leading-0">*</span>
      <tippy v-if="tooltip" class="flex items-center justify-center ml-1">
        <BaseIcon
          name="info"
          class="text-gray-400 group-hover:text-success hover:ring-success text-[18px] base-transition"
        />
        <template #content>
          <slot name="tooltip">{{ $t(tooltip || '') }}</slot>
        </template>
      </tippy>
    </label>
    <div class="relative" v-show="!alert">
      <div
        v-if="icon"
        v-tippy="iconTooltip"
        class="absolute top-0 left-4 h-full flex items-center gap-x-2 text-primary"
      >
        <BaseIcon :name="icon" size="20" class="dark:text-white"></BaseIcon>
      </div>
      <input
        v-if="maska"
        v-model="inputValue"
        v-bind="attrs"
        v-maska
        eager
        :data-maska="maska"
        :placeholder="$t(placeholder || '')"
        :min="min"
        :class="classes"
      />
      <input
        v-else
        v-bind="attrs"
        :placeholder="$t(placeholder || '')"
        v-model="inputValue"
        :class="classes"
        :min="min"
        :pattern="pattern"
      />
      <div  v-if="loading" class="absolute top-0 h-full flex items-center right-4">
        <div class="animate-spin   h-5 w-5 text-primary dark:text-white">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      </div>
     
      <div
        v-else
        class="absolute top-0 h-full flex items-center gap-x-2"
        :class="[type == 'date' ? 'right-10' : type == 'number' ? 'right-6' : 'right-4']"
      >
        
        <a
          v-if="type == 'password' || type == 'password_confirmation'"
          href="#show"
          class=""
          @click.prevent="isShow = !isShow"
        >
          <BaseIcon :name="isShow ? 'visibility_off' : 'visibility'" size="20"></BaseIcon>
        </a>
        <BaseIcon
          v-if="errorMessage"
          name="error_outline"
          size="24"
          class="text-danger dark:text-danger"
        ></BaseIcon>
        <BaseIcon
          v-if="meta.valid && meta.initialValue"
          name="task_alt"
          size="24"
          class="text-success/70 dark:text-success/70 transition duration-150"
        ></BaseIcon>
        <slot />
      </div>
    </div>
    <template v-if="alert">
      <BaseAlert :show="!!isError" type="danger">{{ $t(isError || '') }}</BaseAlert>
    </template>
    <div :class="['min-h-4 pl-4']" v-else-if="!noAlert">
      <span v-if="isError" class="text-danger dark:text-danger text-xs">
        {{ $t(isError || '') }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, toRef } from 'vue';
import { useField } from 'vee-validate';
type Props = {
  label?: string;
  name: string;
  type?: string;
  id?: string;
  placeholder?: string;
  autocomplete?: string;
  pattern?: string;
  modelValue?: string | number | null;
  iconTooltip?: string;
  icon?: string | null;
  step?: string;
  min?: number;
  errors?: Record<string, any>;
  disabled?: boolean;
  noLabelTranslate?: boolean;
  required?: boolean;
  dense?: boolean;
  maska?: string | null;
  tooltip?: string | null;
  noLabel?: boolean;
  alert?: boolean;
  noAlert?: boolean;
  rule?: any | null;
  ghostLabel?: boolean;
  loading?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  type: 'text',
  step: '0.01',
  maska: null,
  rule: null,
});
const isShow = ref(false);
const inputType = computed(() => {
  if (isShow.value) {
    return 'text';
  }
  return props.type;
});

const {
  value: inputValue,
  errorMessage,
  errors,
  meta,
} = useField(toRef(props, 'name'), props.rule, {
  // NOTE: Do not pass initialValue from schema/UI defaults.
  // It can overwrite already-hydrated form values during field registration.
  syncVModel: true,
});

const classes = computed(() => {
  const statisClass =
    'bg-grey-50 border-2 text-base rounded-2xl focus:ring-blue-500/30 placeholder:font-normal placeholder:text-sm focus:border-blue-500/30 block w-full pr-3 outline-hidden ring-2 ring-transparent   dark:focus:ring-blue-500 dark:focus:border-blue-50';
  return [
    statisClass,
    errorMessage.value
      ? 'border-danger/30 dark:border-danger placeholder:text-danger/70 text-danger dark:text-danger bg-danger/5 dark:bg-danger/5'
      : props.disabled
        ? 'border-gray-300 text-primary bg-gray-200'
        : 'border-gray-300 text-primary bg-gray-50 dark:placeholder-gray-400 dark:text-gray-200 dark:bg-gray-700 dark:border-gray-600',
    props.dense ? 'py-2' : 'py-3',
    props.icon ? 'pl-12' : 'pl-3',
  ];
});

const attrs = computed(() => ({
  id: props.id,
  name: props.name,
  disabled: props.disabled,
  step: props.step,
  // placeholder: props.placeholder,
  type: inputType.value,
}));

const isError = computed(() => {
  return errorMessage.value || errors.value.map((e: any) => e.message).join(', ');
});
</script>

<style></style>
